import React, { useEffect, useState } from "react";
import UICustomisationLayoutWithCard from "../../../../base/components/UICustomisationLayoutWithCard";
import { useDarkMode } from "../../../../base/context/darkMode";
import {
  UncontrolledAccordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Row,
} from "reactstrap";
import { Formik } from "formik";
import { useService } from "../../../../base/hooks/useService";
import ConfigService, { ThemeTypes } from "../../../../services/ConfigService";
import Button from "../../../../base/components/Button";
import { BUTTON_COLORS } from "../../../../base/components/Button/appearance";
import joinClassNames from "../../../../base/helpers/joinClassNames";
import * as _ from "lodash";
import FormikColorPicker from "../../../../base/components/FormikColorPicker";
import ToasterService from "../../../../services/ToastService";

export default function AdminPanel() {
  const breadcrumbs = {
    title: "General customisation",
    breadcrumbItems: [],
  };
  const isDarkMode = useDarkMode();
  const themeType = isDarkMode ? ThemeTypes.dark : ThemeTypes.white;
  /**
   * @type {ConfigService}
   */
  const configService = useService(ConfigService);

  const [open, setOpen] = useState("1");
  const [initialValues, setInitialValues] = useState({});
  const [isSubmitting, updateIsSubmitting] = useState(false);
  const [apiConfig, setApiConfig] = useState({});
  const [apiSettings, setApiSettings] = useState({});
  /**
   * @type {ToasterService}
   */
  const toastService = useService(ToasterService);

  const fetchData = () => {
    return Promise.all([
      configService.fetchCustomerConfig(),
      configService.fetchThemeSettings(),
    ]);
  };

  useEffect(() => {
    fetchData().then(([rawConfig, rawThemeSettings]) => {
      const configFields = ["id", "variable", "value"];
      const config = _.chain(rawConfig)
        .get("data")
        .map((item) => _.pick(item, configFields))
        .keyBy("variable")
        .value();

      setApiConfig(config);

      const themeFields = [
        "id",
        "propertyName",
        "fileId",
        "value",
        "file",
        "page",
        "themeType",
      ];
      const themeSettings = _.chain(rawThemeSettings)
        .get("data", [])
        .filter({ themeType: themeType })
        .map((item) => _.pick(item, themeFields))
        .keyBy("propertyName")
        .value();

      setApiSettings(themeSettings);

      setInitialValues((prev) => ({
        ...prev,
        THEME_MODE: config.THEME_MODE.value,
        organisationLogo: themeSettings.organisationLogo.value,
        generalLinksColor: themeSettings.generalLinksColor.value,
        adminSidebarColor: themeSettings.adminSidebarColor.value,
        adminSidebarDefaultFontColor:
          themeSettings.adminSidebarDefaultFontColor.value,
        adminSidebarActiveFontColor:
          themeSettings.adminSidebarActiveFontColor.value,
        adminMainButtonColor: themeSettings.adminMainButtonColor.value,
        adminMainButtonTextColor: themeSettings.adminMainButtonTextColor.value,
        SHOPIFY_PLUS: config.SHOPIFY_PLUS?.value,
      }));
    });
  }, [configService, isDarkMode]);

  const toggle = (id) => {
    if (open === id) {
      setOpen(undefined);
    } else {
      setOpen(id);
    }
  };

  const submitSettings = async (values) => {
    updateIsSubmitting(true);
    try {
      const configToSubmit = _.pick(apiConfig, _.keys(values));
      const settingsToSubmit = _.pick(apiSettings, _.keys(values));

      _.map(settingsToSubmit, (setting) => {
        setting.value = values[setting.propertyName];
        return setting;
      });

      _.map(configToSubmit, (config) => {
        config.value = values[config.variable];
      });

      settingsToSubmit.organisationLogo = _.omit(
        settingsToSubmit.organisationLogo,
        ["value", "file"]
      );

      const updatedSettings = await configService.updateThemeSettings(
        _.values(settingsToSubmit)
      );
      const updatedConfig = await configService.updateCustomerConfig(
        _.values(configToSubmit)
      );

      toastService.success("Settings was successfully uploaded");
    } catch (err) {
      console.error(err);
      toastService.error(err);
    } finally {
      updateIsSubmitting(false);
    }
  };

  return (
    <UICustomisationLayoutWithCard
      breadcrumbs={breadcrumbs}
      cardClassName="ui-customisation"
    >
      <Col lg={6}>
        <Formik
          initialValues={initialValues}
          validateOnBlur
          onSubmit={submitSettings}
          enableReinitialize
        >
          {({ errors, handleSubmit }) => (
            <form
              className={joinClassNames(
                "form-horizontal p-0",
                isSubmitting && "pointer-events-none"
              )}
              onSubmit={handleSubmit}
            >
              <section className="admin-customisation-section">
                <h2>Admin Customisation</h2>
                <FormikColorPicker
                  label="Sidebar color"
                  placeholder=""
                  name="adminSidebarColor"
                  maxLength={255}
                  containerClassName="mt-3"
                />
                <FormikColorPicker
                  label="Sidebar default font color"
                  placeholder=""
                  name="adminSidebarDefaultFontColor"
                  maxLength={255}
                  containerClassName="mt-3"
                />
                <FormikColorPicker
                  label="Sidebar active font color"
                  placeholder=""
                  name="adminSidebarActiveFontColor"
                  maxLength={255}
                  containerClassName="mt-3"
                />
                <FormikColorPicker
                  label="Main button color"
                  placeholder=""
                  name="adminMainButtonColor"
                  maxLength={255}
                  containerClassName="mt-3"
                />
                <FormikColorPicker
                  label="Main button text color"
                  placeholder=""
                  name="adminMainButtonTextColor"
                  maxLength={255}
                  containerClassName="mt-3"
                />
              </section>
              <div className="d-flex justify-content-end mt-5 gap-3">
                <Button
                  color={BUTTON_COLORS.primary}
                  type="submit"
                  disabled={!!Object.keys(errors).length || isSubmitting}
                >
                  Save changes
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </Col>
    </UICustomisationLayoutWithCard>
  );
}
